import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";

export function useBookingActions(onChanged) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null);

  const setStatus = async (b, status) => {
    try {
      const { data } = await api.patch(`/bookings/${b.id}/status`, { status });
      toast.success(`${b.job_number} marked ${status}`);
      onChanged?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const duplicate = (b) => navigate(`/bookings/new?duplicate=${b.id}`);
  const edit = (b) => navigate(`/bookings/${b.id}/edit`);

  const cancel = (b) =>
    setConfirm({
      title: `Cancel booking ${b.job_number}?`,
      body: `${b.customer_name}'s booking will be marked as Cancelled. It stays in the system.`,
      action: "Cancel booking",
      run: () => setStatus(b, "Cancelled"),
    });

  const remove = (b) =>
    setConfirm({
      title: `Permanently delete booking ${b.job_number}?`,
      body: `This removes ${b.customer_name}'s booking completely and cannot be undone.`,
      action: "Delete permanently",
      danger: true,
      run: async () => {
        try {
          await api.delete(`/bookings/${b.id}`);
          toast.success(`Booking ${b.job_number} deleted`);
          onChanged?.(null, b);
        } catch (e) {
          toast.error(formatApiError(e));
        }
      },
    });

  const dialog = (
    <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
          <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-dialog-cancel">Keep booking</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-dialog-confirm"
            className={confirm?.danger ? "bg-rose-600 hover:bg-rose-700" : "bg-[#0E1E38]"}
            onClick={() => confirm?.run()}
          >
            {confirm?.action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { setStatus, duplicate, edit, cancel, remove, dialog };
}
